import { create } from "zustand";
import { downloadAsset } from "@/configurator/data/api/assets";
import { getStartupConfig } from "@/configurator/data/api/configs";
import {
  findActiveModelOption,
  findProduct,
  getStartupProductId,
} from "@/configurator/domain/logic/catalog";
import {
  getDefaultSelectedOptions,
  mergeSharedOptions,
  toggleSelectedOption,
} from "@/configurator/domain/logic/selection";
import type { Id } from "@/configurator/domain/models/types";
import type {
  ConfiguratorCommands,
  ConfiguratorSnapshot,
} from "@/configurator/domain/ports/configuratorState";

const initialConfig = getStartupConfig("demo");
const initialProductId = getStartupProductId(initialConfig.catalog);

type ConfiguratorActions = ConfiguratorCommands & {
  setActiveProduct: (productId: Id) => void;
};

/**
 * The store's state is the domain's snapshot as-is: the port is the contract,
 * and the store is one way of keeping it.
 */
export type ConfiguratorStore = ConfiguratorSnapshot & ConfiguratorActions;

export const useConfiguratorStore = create<ConfiguratorStore>((set, get) => ({
  config: initialConfig,
  activeProductId: initialProductId,
  selectedModelId: initialConfig.model.defaultModelId,
  quantity: 1,
  selectedOptions: getDefaultSelectedOptions(
    findProduct(initialConfig.catalog, initialProductId),
  ),

  setQuantity: (quantity) =>
    set({ quantity: Math.max(1, Math.floor(quantity)) }),

  setActiveProduct: (productId) =>
    set((state) => ({
      activeProductId: productId,
      selectedOptions: getDefaultSelectedOptions(
        findProduct(state.config.catalog, productId),
      ),
    })),

  setActiveModel: (modelId) => set({ selectedModelId: modelId }),

  setOption: (payload) =>
    set((state) => ({
      selectedOptions: {
        ...state.selectedOptions,
        [payload.groupId]: payload.optionId,
      },
    })),

  toggleOption: (payload) =>
    set((state) => ({
      selectedOptions: toggleSelectedOption(
        state.selectedOptions,
        payload.groupId,
        payload.optionId,
      ),
    })),

  applySharedSelection: (selection) =>
    set((state) => {
      const { catalog, model } = state.config;
      const productId = findProduct(catalog, selection.productId)
        ? (selection.productId as Id)
        : state.activeProductId;

      return {
        activeProductId: productId,
        selectedModelId: model.options.some((o) => o.id === selection.modelId)
          ? (selection.modelId as Id)
          : state.selectedModelId,
        selectedOptions: mergeSharedOptions(
          findProduct(catalog, productId),
          selection.selectedOptions,
        ),
      };
    }),

  setConfig: (config) => {
    const productId = getStartupProductId(config.catalog);
    set({
      config,
      activeProductId: productId,
      selectedModelId: config.model.defaultModelId,
      selectedOptions: getDefaultSelectedOptions(
        findProduct(config.catalog, productId),
      ),
    });
  },

  // The store owns the download because the data layer is the only place
  // allowed to reach the network — see Architecture in AGENTS.md.
  downloadActiveModel: async () => {
    const state = get();
    const model = findActiveModelOption(state.config, state.selectedModelId);
    if (!model) return;
    await downloadAsset(model.path);
  },
}));
